import type { ServiceBinding } from '@core/lib/serviceBindings';
import type { Alarm } from '@wisionmonorepo/wision-device-service/src/features/common/alarm/model';
import type { DataResponse as DataResponseV3 } from '@wisionmonorepo/wision-device-service/src/features/common/v3/data/model';
import type { DataResponse } from '@wisionmonorepo/wision-device-service/src/features/common/data/model';
import type { Operation, Status as OperationStatus } from '@wisionmonorepo/wision-device-service/src/features/common/coreOperation/model';
import type { DeviceBase } from '@wisionmonorepo/wision-device-service/src/features/common/device/model';
import { DeviceFilter, SelectedFilters } from '@wisionmonorepo/wision-device-service/src/features/common/filter/model';
import type { Group, EditGroup, CreateGroup } from '@wisionmonorepo/wision-device-service/src/features/common/groups/model';
import type { DeviceOptions } from '@wisionmonorepo/wision-device-service/src/features/common/options/model';
import type { AlarmActionListEntry } from '@wisionmonorepo/wision-device-service/src/features/legacy/alarmAction/model';
import type { InputType } from '@wisionmonorepo/wision-device-service/src/features/legacy/inputs/model';
import type { DeviceSettings } from '@wisionmonorepo/wision-device-service/src/features/legacy/settings/model';
import type { Radar } from '@wisionmonorepo/wision-device-service/src/features/legacy/radar/model';
import type { GetAllMarksResult as MarkersResponse, Marker } from '@wisionmonorepo/wision-device-service/src/features/legacy/marker/model';
import useApiClient, { useRequestInit } from './apiClient';

type ApiVersion = 2 | 3;

export function useDeviceApi(
  baseUrl: string,
  version?: 2,
  serviceBinding?: ServiceBinding,
  sessionCookie?: string
): ReturnType<typeof apiv2>;

export function useDeviceApi(
  baseUrl: string,
  version?: 3,
  serviceBinding?: ServiceBinding,
  sessionCookie?: string
): ReturnType<typeof apiv3>;

export function useDeviceApi(
  baseUrl: string,
  version: ApiVersion = 2,
  serviceBinding: ServiceBinding | null = null,
  sessionCookie: string | null = null
) {
  const apiClient = useApiClient(serviceBinding?.fetch.bind(serviceBinding), true);
  const getRequestInit = useRequestInit(serviceBinding?.fetch, sessionCookie);

  const api = version == 3 ? apiv3 : apiv2;

  return api(baseUrl, apiClient, getRequestInit);
}

const apiv3 = (
  baseUrl: string,
  apiClient: ReturnType<typeof useApiClient>,
  getRequestInit: ReturnType<typeof useRequestInit>
) => {
  const getData = async (id: Array<string>, options?: { records?: number, start?: Date; end?: Date }) => {
    const params = options
      ? new URLSearchParams({
        ...(options.start ? { start: options.start.toISOString() } : {}),
        ...(options.end ? { end: options.end.toISOString() } : {}),
        ...(options.records ? { records: String(options.records) } : {})
      }).toString()
      : undefined;

    return await apiClient<DataResponseV3>(
      `${baseUrl}/api/v3/device/devices/${id}/data${params ? `?${params}` : ''}`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );
  };

  return {
    getData,
  };
};

const apiv2 = (
  baseUrl: string,
  apiClient: ReturnType<typeof useApiClient>,
  getRequestInit: ReturnType<typeof useRequestInit>
) => {
  const editInput = async (id: string, inputType: InputType, index: number, data: Record<string, unknown>) =>
    await apiClient<void>(
      `${baseUrl}/api/v2/device/legacy/devices/${id}/${inputType}/${index}`,
      getRequestInit({
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
    );

  const getAlarmActions = async () =>
    await apiClient<AlarmActionListEntry[]>(
      `${baseUrl}/api/v2/device/legacy/alarm_actions`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getMarkers = async (deviceId: string, index = 0) => await apiClient<MarkersResponse>(
    `${baseUrl}/api/v2/device/legacy/devices/${deviceId}/channel/${index + 1}/markers`,
    getRequestInit({
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
  );

  const createMarker = async (deviceId: string, index = 0, data: Marker) => await apiClient<Marker>(
    `${baseUrl}/api/v2/device/legacy/devices/${deviceId}/channel/${index + 1}/markers`,
    getRequestInit({
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  );

  const editMarker = async (
    deviceId: string,
    marker: number,
    index = 0,
    data: Marker
  ) => await apiClient<Marker>(
    `${baseUrl}/api/v2/device/legacy/devices/${deviceId}/channel/${index + 1}/markers/${marker}`,
    getRequestInit({
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  );

  const deleteMarker = async (deviceId: string, marker: number, index = 0) => await apiClient<void>(
    `${baseUrl}/api/v2/device/legacy/devices/${deviceId}/channel/${index + 1}/markers/${marker}`,
    getRequestInit({
      method: 'DELETE',
      headers: {
        Accept: 'application/json'
      }
    })
  );

  const getRelay = async (id: number) =>
    await apiClient<Array<boolean>>(
      `${baseUrl}/api/v2/device/devices/${id}/relay`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const setRelay = async (id: number, state: Array<boolean>) =>
    await apiClient<void>(
      `${baseUrl}/api/v2/device/devices/${id}/relay`,
      getRequestInit({
        method: 'POST',
        body: JSON.stringify(state),
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getAlarms = async (ids: number[] = []) =>
    await apiClient<Alarm[]>(
      `${baseUrl}/api/v2/device/alarms`,
      getRequestInit({
        method: 'POST',
        body: JSON.stringify({ devices: ids }),
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getRadar = async (id: string, timestamp: string, channel = 0) =>
    await apiClient<Radar>(
      `${baseUrl}/api/v2/device/legacy/devices/${id}/radar/${channel}?timestamp=${timestamp}`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const editRadar = async (id: string, channel: number, data: { reference: string }) =>
    await apiClient<Radar>(
      `${baseUrl}/api/v2/device/legacy/devices/${id}/radar/${channel}`,
      getRequestInit({
        method: 'PUT',
        body: JSON.stringify({
          ChannelDefaultRadar: data.reference,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }));

  const getData = async (id: number, timeInterval?: { start: Date; stop: Date }) =>
    await apiClient<DataResponse>(
      `${baseUrl}/api/v2/device/devices/${id}/data${
        timeInterval ? `?start=${timeInterval.start.toISOString()}&stop=${timeInterval.stop.toISOString()}` : ''
      }`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getSettings = async (id: number) =>
    await apiClient<DeviceSettings>(
      `${baseUrl}/api/v2/device/legacy/devices/${id}/settings`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const editSettings = async (id: string, data: Record<string, unknown>) =>
    await apiClient<DeviceSettings>(
      `${baseUrl}/api/v2/device/legacy/devices/${id}/settings`,
      getRequestInit({
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
    );

  const getOptions = async (id: number) =>
    await apiClient<DeviceOptions>(
      `${baseUrl}/api/v2/device/devices/${id}/options`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getDevices = async (useContext: boolean) =>
    await apiClient<DeviceBase[]>(
      `${baseUrl}/api/v2/device/devices${useContext ? '?use_context=true' : ''}`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getDeviceFilter = async (useContext: boolean, filterOptions: SelectedFilters) =>
    await apiClient<{ devices: DeviceBase[]; filter: DeviceFilter }>(
      `${baseUrl}/api/v2/device/deviceFilter${useContext ? '?use_context=true' : ''}`,
      getRequestInit({
        method: 'POST',
        body: JSON.stringify(filterOptions),
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const getGroups = async (useContext: boolean) =>
    await apiClient<Group[]>(
      `${baseUrl}/api/v2/device/groups${useContext ? '?use_context=true' : ''}`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const editGroup = async (group: EditGroup) =>
    await apiClient<Group[]>(
      `${baseUrl}/api/v2/device/groups`,
      getRequestInit({
        method: 'PUT',
        body: JSON.stringify(group),
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const createGroup = async (group: CreateGroup) =>
    await apiClient<Group[]>(
      `${baseUrl}/api/v2/device/groups`,
      getRequestInit({
        method: 'POST',
        body: JSON.stringify(group),
        headers: {
          Accept: 'application/json',
        },
      })
    );
  
  const deleteGroup = async (groupId: number, organization: number) =>
    await apiClient(
      `${baseUrl}/api/v2/device/groups`,
      getRequestInit({
        method: 'DELETE',
        body: JSON.stringify({ id: groupId, organization }),
        headers: {
          Accept: 'application/json',
        },
      })
    );

  const triggerOperation = async (deviceId: string, operation: Operation) => await apiClient<number>(
    `${baseUrl}/api/v2/device/devices/${deviceId}/operation?operation=${operation}`,
    getRequestInit({
      method: 'POST',
      headers: {
        Accept: 'application/json'
      }
    })
  );

  const pollOperationStatus = async (
    deviceId: string,
    sequence: number,
    operation: Operation
  ) => await apiClient<OperationStatus>(
    `${baseUrl}/api/v2/device/devices/${deviceId}/operation?sequence=${sequence}&operation=${operation}`,
    getRequestInit({
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
  );

  const updateGroupId = async (groupId: number, devices: number[]) =>
    await apiClient(
      `${baseUrl}/api/v2/device/devices/updateGroup`,
      getRequestInit({
        method: 'PUT',
        body: JSON.stringify({ groupId, devices }),
        headers: {
          Accept: 'application/json',
        }
      })
    );

  return {
    editInput,
    editSettings,
    getAlarms,
    getAlarmActions,
    getMarkers,
    createMarker,
    editMarker,
    deleteMarker,
    getDevices,
    getRelay,
    setRelay,
    getData,
    getRadar,
    editRadar,
    getGroups,
    getOptions,
    getSettings,
    getDeviceFilter,
    editGroup,
    createGroup,
    deleteGroup,
    updateGroupId,
    triggerOperation,
    pollOperationStatus,
  };
};
